import React, { useEffect, useMemo, useState } from 'react';
import { Pagination, Table } from '@bfe/moly';
import { Modal } from '@web3/base-components';
import { useFm, useParams } from '@web3/base-hooks';
import { formatNumber } from '@web3/base-utils';
import { formatAmount } from '@/utils';
import { getSpotTradeRank } from '@/services/home';
import { initialWindowType } from '@/hooks/media';
import {
  PORJECT_LIST_RANK_1_IMG,
  PORJECT_LIST_RANK_2_IMG,
  PORJECT_LIST_RANK_3_IMG,
} from '@/constants';

import styles from './index.module.scss';

type Props = {
  visible: boolean;
  projectName?: string; // 项目名称
  queryType?: 0 | 1; // 0 当前数据， 1 昨日数据
  totalPoints?: string; // 总积分
  yesterdayRank?: string; // 昨日排名
  yesterdayIncrease?: string; // 昨日增长
  onClose?: () => void;
};

const PAGE_SIZE = 10;

const YestdayPoints = ({
  visible = false,
  projectName,
  queryType = 0,
  totalPoints = '',
  yesterdayRank = '',
  yesterdayIncrease = '',
  onClose,
}: Props) => {
  const fm = useFm();
  const { isMobile } = initialWindowType;
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState([]);
  const activityCode = useParams().activityCode;

  useEffect(() => {
    if (visible) {
      setLoading(true);
      getSpotTradeRank({
        queryType,
        activityCode,
      })
        .then((res) => {
          if (res.success && res.result) {
            setData(res.result);
            setPage(1);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [visible]);

  const currentList = useMemo(() => {
    return data?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) || [];
  }, [data, page]);

  const columns = [
    {
      accessorKey: 'rank',
      header: () => <span>{fm('ranking')}</span>,
      cell: ({ row }) => {
        const rank = (page - 1) * PAGE_SIZE + row.index + 1;
        if (rank === 1) {
          return (
            <img
              className="w-[24px] h-[24px]"
              src={PORJECT_LIST_RANK_1_IMG}
              alt="rank1"
            />
          );
        } else if (rank === 2) {
          return (
            <img
              className="w-[24px] h-[24px]"
              src={PORJECT_LIST_RANK_2_IMG}
              alt="rank2"
            />
          );
        } else if (rank === 3) {
          return (
            <img
              className="w-[24px] h-[24px]"
              src={PORJECT_LIST_RANK_3_IMG}
              alt="rank3"
            />
          );
        } else {
          return <div>{(page - 1) * PAGE_SIZE + row.index + 1}</div>;
        }
      },
    },
    {
      accessorKey: 'name',
      header: <span>{fm('projectName')}</span>,
    },
    {
      accessorKey: 'spotTradeAmount',
      header: () => <span>{fm('tradingVolYesterday')}</span>,
      cell: ({ row }) => (
        <div>${formatAmount(row.getValue('spotTradeAmount'))}</div>
      ),
    },
    {
      accessorKey: 'point',
      header: () => <span>{fm('wsotPt')}</span>,
      cell: ({ row }) => <div>{formatNumber(row.getValue('point'))}</div>,
    },
  ];

  return (
    <Modal
      width={640}
      className={styles.modalWrapper}
      contextWrapper={styles.modalContextWrapper}
      isMobile={isMobile}
      direction={!isMobile ? 'center' : 'bottom'}
      title={
        projectName
          ? fm('projectVotingPoints', {
              projectName,
            })
          : fm('spotTradingVolLeaderboard')
      }
      okText={false}
      cancelText={false}
      onCancel={() => {
        onClose?.();
      }}
      open={visible}
      onOpenAutoFocus={(e) => e.preventDefault()}
    >
      <div className={styles.wrapper}>
        {queryType === 1 && (
          <>
            <div className={styles.top}>
              <div className="flex flex-col items-start">
                <div className={styles.label}>{fm('totalPoints')}</div>
                <div className={styles.text}>{totalPoints}</div>
              </div>
              <div className="flex flex-col items-end">
                {!!+yesterdayIncrease && (
                  <div className={styles.smallText}>
                    {fm('yesterdayRanking') + ` `}
                    <span className={styles.value}>{yesterdayRank}</span>
                  </div>
                )}
                {!!+yesterdayIncrease && (
                  <div className={styles.smallText}>
                    {fm('increase') + ` `}
                    <span className={styles.value}>{yesterdayIncrease}</span>
                  </div>
                )}
              </div>
            </div>
            <div className={styles.tableTitle}>{fm('yestSpotTradeVol')}</div>
          </>
        )}
        <div className={styles.tableWrapper}>
          <Table
            columns={columns as any}
            data={currentList}
            loading={loading}
          />
          <Pagination
            total={data?.length || 0}
            pageSize={PAGE_SIZE}
            current={page}
            onChange={(curPage) => {
              setPage(curPage);
            }}
            hideOnSinglePage
            showQuickJumper={!isMobile}
          />
        </div>
      </div>
    </Modal>
  );
};

export default YestdayPoints;
